import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { motion, type AnimationControls } from 'framer-motion';
import {
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_TOOL_FEATURES,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallPdfPage,
  PaywallPriceText,
  PaywallThumbPlaceholder,
} from './components';
import {
  ReadyCheckIcon,
  StepCheckIcon,
  TrialRocketIcon,
  ZoomInIcon,
} from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { splitFileName } from './utils';

const FeatureGrid = () => (
  <div className="flex w-full flex-col gap-4">
    <p className="text-[20px] font-bold leading-normal text-[#292A33]">
      Everything unlocks the moment you start
    </p>
    <div className="grid w-full grid-cols-3 gap-1 lg:gap-3">
      {PAYWALL_TOOL_FEATURES.map(({ title, description, color, Icon }) => (
        <div
          key={title}
          className="flex flex-col gap-2 break-words rounded-[10px] bg-white p-3"
        >
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-[#F6F7F8]"
            style={{ color }}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </span>
          <div className="flex w-full flex-col gap-0.5">
            <p className="text-[16px] font-semibold leading-normal text-[#292A33] lg:text-[18px]">
              {title}
            </p>
            <p className="text-[13px] leading-normal text-[#787887] lg:text-[15px]">
              {description}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DocumentThumb = ({
  file,
  extension,
}: {
  file?: File | null;
  extension: string;
}) => {
  const isPdf = file?.type === 'application/pdf';
  const isImage = Boolean(file?.type?.startsWith('image/'));
  const imageUrl = useMemo(
    () => (file && isImage ? URL.createObjectURL(file) : ''),
    [file, isImage]
  );

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  return (
    <div className="relative flex h-32 w-24 shrink-0 items-center justify-center rounded-[8px] bg-[#E5E6EB] lg:w-32">
      <div className="h-[95px] w-[68px] overflow-hidden rounded-[6px] bg-[#EBEBEB] shadow-[0px_4.8px_4.8px_0px_rgba(0,0,0,0.16)]">
        {isPdf && file ? (
          <PaywallPdfPage
            file={file}
            width={68}
            fallback={
              <PaywallThumbPlaceholder className="h-[95px] w-[68px] border-transparent" />
            }
          />
        ) : isImage && imageUrl ? (
          <img
            src={imageUrl}
            alt={extension}
            className="h-full w-full object-cover"
          />
        ) : (
          <PaywallThumbPlaceholder className="h-[95px] w-[68px] border-transparent" />
        )}
      </div>
      <span
        className={clsx(
          'pointer-events-none absolute left-[22px] top-5 flex h-[19px] items-center rounded-[6px] border-2 border-white px-1 text-[10px] font-medium uppercase leading-none text-white',
          PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400'
        )}
      >
        {extension}
      </span>
      <span className="absolute left-3 top-[84px] flex h-8 w-8 items-center justify-center overflow-hidden rounded-[6px] bg-[#EEEEF2]">
        <ZoomInIcon className="h-[18px] w-[18px]" aria-hidden />
      </span>
    </div>
  );
};

const SummaryRow = ({
  children,
  divider = true,
}: {
  children: React.ReactNode;
  divider?: boolean;
}) => (
  <div
    className={clsx(
      'flex w-full items-center justify-between py-3 text-[17px] leading-normal lg:py-4',
      divider && 'border-b border-[#EBEBEB]'
    )}
  >
    {children}
  </div>
);

const OrderSummary = ({
  isOnlyPayment,
  hasTrial,
  planName,
  planSubtitle,
  dueTodayAmountText,
  recurringAmountText,
  firstChargeDate,
  recurringAdjective,
  documentName,
  documentExtension,
  documentFile,
}: PaywallDocumentProps & {
  isOnlyPayment: boolean;
  hasTrial: boolean;
  planName: string;
  planSubtitle: string;
  dueTodayAmountText: string;
  recurringAmountText: string;
  firstChargeDate: string;
  recurringAdjective: string;
}) => {
  const { base, suffix } = splitFileName(documentName, documentExtension);

  return (
    <div className="flex w-full flex-col gap-4">
      <p className="text-[20px] font-bold leading-normal text-[#292A33]">
        Order Summary
      </p>
      <div className="flex w-full flex-col overflow-hidden rounded-[20px] bg-white">
        {!isOnlyPayment && (
          <div className="flex w-full items-center gap-4 border-b border-[#EBEBEB] p-4">
            <DocumentThumb file={documentFile} extension={documentExtension} />
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
              <p
                title={documentName}
                className="flex w-full text-[17px] font-semibold leading-normal text-[#292A33]"
              >
                <span className="min-w-0 truncate">{base}</span>
                <span className="shrink-0">{suffix}</span>
              </p>
              <div className="flex w-full items-center gap-1">
                <ReadyCheckIcon className="h-6 w-6 shrink-0" aria-hidden />
                <p className="min-w-0 flex-1 text-[17px] font-medium leading-normal text-[#009E5E] lg:flex-none lg:whitespace-nowrap">
                  Your document is ready!
                </p>
              </div>
            </div>
          </div>
        )}
        <div
          className={clsx(
            'flex w-full flex-col',
            isOnlyPayment ? 'p-4 lg:px-6' : 'px-4 pb-6 lg:px-6'
          )}
        >
          <SummaryRow>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 lg:whitespace-nowrap">
              <span className="font-semibold text-[#292A33]">{planName}</span>
              {hasTrial && (
                <span className="text-[#787887]">{planSubtitle}</span>
              )}
            </div>
            <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#4B7EFF]">
              <img
                src={
                  hasTrial
                    ? '/images/paywall1/gift.png'
                    : '/images/paywall1/rocket.svg'
                }
                alt=""
                className="h-6 w-6"
              />
            </span>
          </SummaryRow>
          <SummaryRow>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 lg:whitespace-nowrap">
              <span className="font-semibold text-[#292A33]">Due today</span>
              {hasTrial && <span className="text-[#787887]">Trial</span>}
            </div>
            <PaywallPriceText
              value={dueTodayAmountText}
              className="ml-4 shrink-0 whitespace-nowrap leading-normal text-[#009E5E]"
            />
          </SummaryRow>
          {hasTrial && (
            <SummaryRow>
              <span className="min-w-0 flex-1 text-[#292A33] lg:whitespace-nowrap">
                First {recurringAdjective} charge {firstChargeDate}
              </span>
              <span className="ml-4 shrink-0 whitespace-nowrap font-semibold text-[#292A33]">
                {recurringAmountText}
              </span>
            </SummaryRow>
          )}
          <SummaryRow>
            <span className="flex-1 whitespace-nowrap text-[#787887]">
              Billing period
            </span>
            <span className="flex-1 text-right font-semibold text-[#292A33] lg:flex-initial">
              Auto-renews at {recurringAmountText} {recurringAdjective} until
              cancelled
            </span>
          </SummaryRow>
          <SummaryRow divider={false}>
            <span className="flex-1 whitespace-nowrap text-[#787887]">
              Cancel
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold text-[#292A33]">
              Through your account
            </span>
          </SummaryRow>
        </div>
      </div>
    </div>
  );
};

const FullScreenPaywall1Layout = ({
  title,
  isOnlyPayment,
  hasTrial,
  planName,
  planSubtitle,
  dueTodayAmountText,
  recurringAmountText,
  firstChargeDate,
  recurringAdjective,
  documentName,
  documentExtension,
  documentFile,
  consentChecked,
  showConsentError,
  consentText,
  onConsentChange,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
  consentRef,
  shakeControls,
  onConsentRequired,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    title: string;
    isOnlyPayment: boolean;
    hasTrial: boolean;
    planName: string;
    planSubtitle: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    firstChargeDate: string;
    recurringAdjective: string;
    consentChecked: boolean;
    showConsentError: boolean;
    consentText: React.ReactNode;
    onConsentChange: (checked: boolean) => void;
    consentRef: React.RefObject<HTMLDivElement>;
    shakeControls: AnimationControls;
    onConsentRequired: () => void;
  }) => (
  <div className="flex w-full shrink-0 grow flex-col bg-[#F6F7F8] font-barlow text-[#292A33] lg:bg-white">
    <div className="w-full border-b border-[#EEEEF2] bg-white px-4 lg:px-6">
      <div className="mx-auto flex min-h-[72px] w-full max-w-[1216px] items-center gap-3 py-3 lg:h-24 lg:gap-4 lg:py-0">
        <img
          src="/meta/icons/genius.svg"
          alt="PDF Genius"
          className="h-8 w-[25.6px] shrink-0"
        />
        <p className="text-[22px] font-bold leading-normal text-[#292A33] lg:whitespace-nowrap lg:text-[28px]">
          {title}
        </p>
      </div>
    </div>

    <div className="flex w-full flex-1 flex-col gap-4 p-4 lg:flex-row lg:gap-0 lg:p-0">
      <section className="w-full lg:flex lg:w-1/2 lg:justify-end lg:pb-[50px] lg:pl-6 lg:pr-10 lg:pt-6">
        <div className="w-full lg:max-w-[528px]">
          <motion.div
            ref={consentRef}
            animate={shakeControls}
            className="flex w-full flex-col gap-4"
          >
            <div className="flex items-center gap-3">
              <p className="whitespace-nowrap text-[20px] font-bold leading-normal text-[#292A33]">
                Payment Details
              </p>
              {hasTrial && (
                <span className="flex shrink-0 items-center justify-center gap-1 overflow-hidden rounded-[6px] bg-[#DAF6ED] p-1.5 text-[#009E5E]">
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <TrialRocketIcon className="h-3 w-3" aria-hidden />
                  </span>
                  <span className="whitespace-nowrap text-[12px] font-medium leading-normal">
                    {planSubtitle.toUpperCase()}!
                  </span>
                </span>
              )}
            </div>
            {showConsentError && (
              <p className="text-[14px] leading-5 text-[#F43F3F]">
                You must check the box below
              </p>
            )}
            <label className="flex w-full cursor-pointer items-start gap-3">
              <span className="relative flex h-5 w-5 shrink-0">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(event) => onConsentChange(event.target.checked)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded-[5px] border border-[#787887] bg-white checked:border-[#4B7EFF] checked:bg-[#4B7EFF]"
                />
                <StepCheckIcon
                  className="pointer-events-none absolute inset-0 hidden h-5 w-5 text-white peer-checked:block"
                  aria-hidden
                />
              </span>
              <span className="min-w-0 flex-1 text-[14px] leading-5 text-[#292A33]">
                {consentText}
              </span>
            </label>
          </motion.div>
          {checkoutMethod && (
            <div className="relative mt-4 lg:mt-8">
              <PaywallCheckoutFrame
                checkoutContainerRef={checkoutContainerRef}
                paymentStatus={paymentStatus}
                minHeight={checkoutMethod === 'card' ? 450 : 560}
                statusClassName="text-[#6D6D6D]"
              >
                {!consentChecked && (
                  <button
                    type="button"
                    aria-label="You must check the box above to subscribe"
                    onClick={onConsentRequired}
                    className="absolute inset-0 z-10 cursor-pointer bg-transparent"
                  />
                )}
              </PaywallCheckoutFrame>
            </div>
          )}
        </div>
      </section>

      <aside className="w-full lg:w-1/2 lg:border-l lg:border-[#EEEEF2] lg:bg-[#F6F7F8] lg:pb-[50px] lg:pl-10 lg:pr-6 lg:pt-6">
        <div className="flex w-full flex-col gap-4 lg:max-w-[528px] lg:gap-8">
          <OrderSummary
            isOnlyPayment={isOnlyPayment}
            hasTrial={hasTrial}
            planName={planName}
            planSubtitle={planSubtitle}
            dueTodayAmountText={dueTodayAmountText}
            recurringAmountText={recurringAmountText}
            firstChargeDate={firstChargeDate}
            recurringAdjective={recurringAdjective}
            documentName={documentName}
            documentExtension={documentExtension}
            documentFile={documentFile}
          />
          <FeatureGrid />
        </div>
      </aside>
    </div>
  </div>
);

export default FullScreenPaywall1Layout;
